import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import _ from 'underscore';

import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { fillViewPort, vizClass, vizStyle, vizWithBreadcrumbStyle } from '~src/style/VizViewerStyles';

import { FeatureFlags } from '../../../ts/FeatureFlags';
import { VizType } from '../../../ts/models/Viz';
import { setVizPath } from '../../actions/spaces/SetVizPathAction';
import { VizPathInitialState } from '../../reducers/VizPathReducer';
import { selectVizPath } from '../../selectors/VizPathSelector';
import { MapDispatchToProps, MapStateToProps } from '../../utils/ConnectUtils';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { VizProps, VizView } from '../VizSpace/Viz';
import { VizUrl } from '../VizSpace/VizUrlHelpers';
import { AdminAnalysisVizSpaceBreadcrumbsAndTitleUpdater } from './AdminAnalysisVizSpaceBreadcrumbsAndTitleUpdater';

export const SiteAdminAnalysisVizPathPrefix = 'tabbed_site_views/';
export const ServerAdminAnalysisVizPathPrefix = 'tabbed_admin_views/';
export const SiteAdminBackgroundJobsVizPathPrefix = 'background_jobs_site/';
export const ServerAdminBackgrounderDelaysVizPathPrefix = 'backgrounder_delays/';
export const ServerAdminCurrentlyRunningJobsVizPathPrefix = 'currently_running_jobs/';
export const SiteAdminBackgrounderDashboardVizPathPrefix = 'backgrounder_dashboard_site/';
export const ServerAdminBackgrounderDashboardVizPathPrefix = 'backgrounder_dashboard/';
export const ServerAdminStaleContentVizPathPrefix = 'stale_content/';
export const ServerAdminStaleContentExtensionVizPathPrefix = 'stale_content_extension/';
export const SiteAdminStaleContentVizPathPrefix = 'stale_content_site_admin/';
export const SiteAdminStaleContentExtensionVizPathPrefix = 'stale_content_extension_site_admin/';
export const ServerAdminServerOverviewVizPathPrefix = 'server_overview/';
export const SiteAdminServerOverviewVizPathPrefix = 'server_overview_site_admin/';
export const SiteAdminAskDataUsageDashboardVizPathPrefix = 'askdata_dashboard_site/';
export const ServerAdminAskDataDashboardVizPathPrefix = 'askdata_dashboard_server/';
export const SiteAdminDqwAuditDashboardVizPathPrefix = 'dqw_audit_dashboard_site/';
export const ServerAdminDqwAuditDashboardVizPathPrefix = 'dqw_audit_dashboard_server/';

export interface AdminAnalysisVizSpaceProviderOwnProps {
  readonly sheetUrlId: AdminAnalysisVizSpaceBreadcrumbsAndTitleUpdater.AdminViewName;
  readonly routeLocationSearch: string;
  readonly urls: SpacesSiteUrls;
  readonly isServerAnalysis: boolean;
  readonly renderSpace: (ref: React.Ref<HTMLDivElement>) => JSX.Element;
}

export interface AdminAnalysisVizSpaceProviderStateProps {
  readonly vizPath: string;
}

export interface AdminAnalysisVizSpaceProviderDispatchProps {
  readonly setVizPath: (vizPath: string) => void;
}

export const renderAdminAnalysisVizSpace = (ref: React.Ref<HTMLDivElement>) => {
  // this structure is intended to keep the same structure of the existing viz container 
  // changes to this will easily break tests
  return (
    <div style={vizWithBreadcrumbStyle} id={'viz-container'}>
      <div style={fillViewPort}>
        <div ref={ref} style={vizStyle} className={vizClass} id={'viz'} tb-test-id={'viz'}> </div>
      </div>
    </div>
  );
};

export function getAdminVizPathPrefix(sheetUrlId: AdminAnalysisVizSpaceBreadcrumbsAndTitleUpdater.AdminViewName, isServerAnalysis: boolean): string {
  switch (sheetUrlId) {
    case 'BackgrounderDashboard':
      return isServerAnalysis
        ? ServerAdminBackgrounderDashboardVizPathPrefix
        : SiteAdminBackgrounderDashboardVizPathPrefix;
    case 'StaleContent':
      return isServerAnalysis
        ? (FeatureFlags.staleContentExtensionEnabled ? ServerAdminStaleContentExtensionVizPathPrefix : ServerAdminStaleContentVizPathPrefix)
        : (FeatureFlags.staleContentExtensionEnabled ? SiteAdminStaleContentExtensionVizPathPrefix   : SiteAdminStaleContentVizPathPrefix);
    case 'ServerOverview':
      return ServerAdminServerOverviewVizPathPrefix;
    case 'ServerOverviewSiteAdmin':
      return SiteAdminServerOverviewVizPathPrefix;
    case 'AskDataUsage':
      return isServerAnalysis ? ServerAdminAskDataDashboardVizPathPrefix : SiteAdminAskDataUsageDashboardVizPathPrefix;
    case 'AskDataPerformance':
      return ServerAdminAskDataDashboardVizPathPrefix;
    case 'AuditDqwDashboard':
      return isServerAnalysis ? ServerAdminDqwAuditDashboardVizPathPrefix : SiteAdminDqwAuditDashboardVizPathPrefix;
    default:
      return isServerAnalysis ? ServerAdminAnalysisVizPathPrefix : SiteAdminAnalysisVizPathPrefix;
  }
}

export type AdminAnalysisVizSpaceProviderProps = AdminAnalysisVizSpaceProviderOwnProps & AdminAnalysisVizSpaceProviderStateProps & AdminAnalysisVizSpaceProviderDispatchProps;

export class AdminAnalysisVizSpaceProvider extends React.Component<AdminAnalysisVizSpaceProviderProps> {

  private vizContainerRef = React.createRef<HTMLDivElement>();
  private viz: tb.IViz = undefined; // the actual viz object
  private vizProps: VizProps = undefined;
  private server = getSessionManagementServiceInstance();

  public constructor(props: AdminAnalysisVizSpaceProviderProps) {
    super(props);
    this.state = {
      hasError: false
    };
  }

  public render() {
    return this.props.renderSpace(this.vizContainerRef);
  }

  public componentDidMount() {
    // initial viz path for analysis vizzes
    this.props.setVizPath(getAdminVizPathPrefix(this.props.sheetUrlId, this.props.isServerAnalysis) + this.props.sheetUrlId);
    this.loadViz();
  }

  private loadViz = () => {
    if (this.vizContainerRef.current) {
      this.viz && this.viz.dispose();
      this.viz = VizView.loadViz(this.vizProps || this.initVizViewerProps(), this.vizContainerRef.current);
    }
  }

  private initVizViewerProps = (): VizProps => {
    const props = {} as VizProps;

    const vizPathFromSheetUrlId = getAdminVizPathPrefix(this.props.sheetUrlId, this.props.isServerAnalysis) + this.props.sheetUrlId;
    props.viz = {
      path: vizPathFromSheetUrlId,
      vizType: VizType.Admin,
      params: VizUrl.getVizParams(this.props.routeLocationSearch),
      siteUrlName: this.server.sessionSiteUrlName(),
      instanceId: VizUrl.getInstanceId(this.props.routeLocationSearch),
      isExplicitOriginalViz: VizUrl.getIsExplicitOriginalVizFromUrl(window.location.href)
    };
    // setting these as noop to avoid errors when tableau.viz tries to access them
    props.dalert = undefined;
    props.onVizInstanceIdChange = _.noop;
    props.onVizPathChange = _.noop;
    props.onVizIsExplicitOriginalVizChange = _.noop;
    props.clearDataAlertRoute = _.noop;

    this.vizProps = props;
    return props;
  }

  public componentWillUnmount() {
    this.props.setVizPath('');
    this.viz && this.viz.dispose();
  }

  public componentDidUpdate(prevProps: AdminAnalysisVizSpaceProviderProps) {
    const vizPathFromSheetUrlId = getAdminVizPathPrefix(this.props.sheetUrlId, this.props.isServerAnalysis) + this.props.sheetUrlId;
    if (prevProps.sheetUrlId !== this.props.sheetUrlId && vizPathFromSheetUrlId !== this.props.vizPath) {
      this.props.setVizPath(getAdminVizPathPrefix(this.props.sheetUrlId, this.props.isServerAnalysis) + this.props.sheetUrlId);
      this.initVizViewerProps();
      this.loadViz();
    }
  }

  public shouldComponentUpdate(nextProps: AdminAnalysisVizSpaceProviderProps) {
    if(this.props.isServerAnalysis !== nextProps.isServerAnalysis) {
      return true;
    }

    if (nextProps.vizPath !== VizPathInitialState &&
        nextProps.vizPath !== this.props.vizPath &&
        nextProps.vizPath !== (getAdminVizPathPrefix(this.props.sheetUrlId, this.props.isServerAnalysis) + nextProps.sheetUrlId)) {
          // vizPath changed via user clicking on viz tab (that's why the url does not match the new vizPath-- see tbViz),
          // so update the url and don't rerender
          return false;
    }

    return true;
  }
}

const mapStateToProps: MapStateToProps<AdminAnalysisVizSpaceProviderStateProps> = (appState) => ({
  vizPath: selectVizPath(appState)
});

const mapDispatchToProps: MapDispatchToProps<AdminAnalysisVizSpaceProviderDispatchProps> = (dispatch) => bindActionCreators({
  setVizPath
}, dispatch);

export const ConnectedAdminAnalysisVizSpaceProvider: React.ComponentClass<AdminAnalysisVizSpaceProviderOwnProps>
  = connect(mapStateToProps, mapDispatchToProps)(AdminAnalysisVizSpaceProvider);
